import React, { useState, useEffect } from 'react';
import {
  Settings, 
  X, 
  Palette, 
  Image as ImageIcon,
  Upload,
  RotateCcw,
  Save,
  Eye,
  EyeOff,
  ChevronDown,
  Sparkles
} from 'lucide-react';
import { useBrand } from '@/context/BrandContext';
import { ImageUploadModal } from '../image-upload';

interface SectionCustomizerProps {
  isOpen: boolean;
  onClose: () => void;
  isGlobalDarkMode: boolean;
  sectionIndex: number;
}

interface SectionCustomization {
  backgroundType: 'color' | 'image' | 'gradient';
  backgroundColor: string;
  backgroundImage: string;
  backgroundPosition: string;
  backgroundSize: string;
  backgroundRepeat: string;
  // Gradient properties
  gradientFrom: string;
  gradientTo: string;
  gradientAngle: number;
  overlayEnabled: boolean;
  overlayColor: string;
  overlayOpacity: number;
}

export const SectionCustomizer: React.FC<SectionCustomizerProps> = ({
  isOpen,
  onClose,
  isGlobalDarkMode,
  sectionIndex
}) => {
  const { state, dispatch } = useBrand();
  const section = state.sections[sectionIndex];
  
  // Applied state (last saved state)
  const [appliedCustomization, setAppliedCustomization] = useState<SectionCustomization>({
    backgroundType: 'color',
    backgroundColor: 'transparent',
    backgroundImage: '',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    gradientFrom: '#8b5cf6',
    gradientTo: '#06b6d4',
    gradientAngle: 135,
    overlayEnabled: false,
    overlayColor: '#000000',
    overlayOpacity: 50
  });

  // Preview state (temporary/draft state)
  const [previewCustomization, setPreviewCustomization] = useState<SectionCustomization>({
    backgroundType: 'color',
    backgroundColor: 'transparent',
    backgroundImage: '',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    gradientFrom: '#8b5cf6',
    gradientTo: '#06b6d4',
    gradientAngle: 135,
    overlayEnabled: false,
    overlayColor: '#000000',
    overlayOpacity: 50
  });

  // Brand color options for reset
  const [showBrandColorOptions, setShowBrandColorOptions] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  // Initialize states from section data
  useEffect(() => {
    if (section && section.customization) {
      const currentCustomization = {
        backgroundType: section.customization.backgroundType || 'color',
        backgroundColor: section.customization.backgroundColor || 'transparent',
        backgroundImage: section.customization.backgroundImage || '',
        backgroundPosition: section.customization.backgroundPosition || 'center',
        backgroundSize: section.customization.backgroundSize || 'cover',
        backgroundRepeat: section.customization.backgroundRepeat || 'no-repeat',
        gradientFrom: section.customization.gradientFrom || '#8b5cf6',
        gradientTo: section.customization.gradientTo || '#06b6d4',
        gradientAngle: section.customization.gradientAngle || 135,
        overlayEnabled: section.customization.overlayEnabled || false,
        overlayColor: section.customization.overlayColor || '#000000',
        overlayOpacity: section.customization.overlayOpacity || 50
      };
      
      setAppliedCustomization(currentCustomization);
      setPreviewCustomization(currentCustomization);
    } else if (section) {
      // Initialize from existing styles
      const defaultCustomization = {
        backgroundType: 'color' as const,
        backgroundColor: section.styles?.backgroundColor || 'transparent',
        backgroundImage: '',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        gradientFrom: '#8b5cf6',
        gradientTo: '#06b6d4',
        gradientAngle: 135,
        overlayEnabled: false,
        overlayColor: '#000000',
        overlayOpacity: 50
      };
      
      setAppliedCustomization(defaultCustomization);
      setPreviewCustomization(defaultCustomization);
    }
  }, [section, isOpen]);

  // Update preview setting (does not affect actual section)
  const updatePreviewSetting = (key: keyof SectionCustomization, value: string | number | boolean) => {
    const newPreviewCustomization = { ...previewCustomization, [key]: value };
    setPreviewCustomization(newPreviewCustomization);
    
    // Don't apply changes to section until Apply is clicked
    // applyCustomizationToSection(newPreviewCustomization, false);
  };

  // Update gradient settings
  const updateGradient = (from: string, to: string, angle: number) => {
    const newPreviewCustomization = {
      ...previewCustomization,
      gradientFrom: from,
      gradientTo: to,
      gradientAngle: angle
    };
    setPreviewCustomization(newPreviewCustomization);
    // Don't apply changes to section until Apply is clicked
    // applyCustomizationToSection(newPreviewCustomization, false);
  };

  // Handle image selection from modal
  const handleImageSelect = (imageUrl: string, imageData?: any) => {
    const newPreviewCustomization = { 
      ...previewCustomization, 
      backgroundType: 'image',
      backgroundImage: imageUrl 
    };
    setPreviewCustomization(newPreviewCustomization);
    // Don't apply changes to section until Apply is clicked
    // applyCustomizationToSection(newPreviewCustomization, false);
    setShowImageModal(false);
  };

  // Helper function to convert hex to rgba
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // Generate background style based on customization
  const generateBackgroundStyle = (customizationToUse: SectionCustomization) => {
    const baseStyle: any = {
      // Ensure proper positioning for overlay
      position: 'relative'
    };
    
    if (customizationToUse.backgroundType === 'color') {
      // Clear any existing image or gradient
      baseStyle.backgroundImage = 'none';
      baseStyle.backgroundColor = customizationToUse.backgroundColor === 'transparent' ? 'transparent' : customizationToUse.backgroundColor;
    } else if (customizationToUse.backgroundType === 'gradient') {
      // Clear solid color and apply gradient
      baseStyle.backgroundColor = 'transparent';
      baseStyle.backgroundImage = `linear-gradient(${customizationToUse.gradientAngle}deg, ${customizationToUse.gradientFrom}, ${customizationToUse.gradientTo})`;
    } else if (customizationToUse.backgroundType === 'image' && customizationToUse.backgroundImage) {
      // Clear solid color and apply image
      baseStyle.backgroundColor = 'transparent';
      baseStyle.backgroundImage = `url(${customizationToUse.backgroundImage})`;
      baseStyle.backgroundPosition = customizationToUse.backgroundPosition;
      baseStyle.backgroundSize = customizationToUse.backgroundSize;
      baseStyle.backgroundRepeat = customizationToUse.backgroundRepeat;
    }

    // Apply overlay if enabled and opacity > 0
    if (customizationToUse.overlayEnabled && customizationToUse.overlayOpacity > 0) {
      const overlayRgba = hexToRgba(customizationToUse.overlayColor, customizationToUse.overlayOpacity / 100);
      
      if (customizationToUse.backgroundType === 'image' && customizationToUse.backgroundImage) {
        // For images, combine background image with overlay using linear-gradient
        baseStyle.backgroundImage = `linear-gradient(${overlayRgba}, ${overlayRgba}), url(${customizationToUse.backgroundImage})`;
      } else if (customizationToUse.backgroundType === 'gradient') {
        // For gradients, combine gradient with overlay
        const gradientValue = `linear-gradient(${customizationToUse.gradientAngle}deg, ${customizationToUse.gradientFrom}, ${customizationToUse.gradientTo})`;
        baseStyle.backgroundImage = `linear-gradient(${overlayRgba}, ${overlayRgba}), ${gradientValue}`;
      } else {
        // For solid colors, create overlay using linear-gradient
        const bgColor = customizationToUse.backgroundColor === 'transparent' ? 'transparent' : customizationToUse.backgroundColor;
        baseStyle.backgroundImage = `linear-gradient(${overlayRgba}, ${overlayRgba}), linear-gradient(${bgColor}, ${bgColor})`;
      }
    }
    
    return baseStyle;
  };

  // Apply customization to section
  const applyCustomizationToSection = (customizationToApply: SectionCustomization, addToHistory = true) => {
    const backgroundStyle = generateBackgroundStyle(customizationToApply);
    
    const updatedSection = {
      ...section,
      customization: customizationToApply,
      styles: {
        ...section.styles,
        ...backgroundStyle,
        // Apply consistent 60px padding
        padding: '60px',
        paddingTop: '60px',
        paddingRight: '60px',
        paddingBottom: '60px',
        paddingLeft: '60px'
      }
    };

    if (addToHistory) {
      dispatch({
        type: 'UPDATE_SECTION',
        payload: { index: sectionIndex, section: updatedSection }
      });
    } else {
      // Update without adding to history (for real-time preview)
      const newSections = [...state.sections];
      newSections[sectionIndex] = updatedSection;
      dispatch({ type: 'SET_SECTIONS', payload: newSections });
    }
  };

  // Get brand colors for reset options
  const getBrandColors = () => {
    const brandColors = [];
    
    if (state.brandData?.colors?.primary) {
      brandColors.push({
        name: 'Primary',
        value: state.brandData.colors.primary,
        description: 'Brand primary color'
      });
    }
    
    if (state.brandData?.colors?.secondary) {
      brandColors.push({
        name: 'Secondary', 
        value: state.brandData.colors.secondary,
        description: 'Brand secondary color'
      });
    }
    
    if (state.brandData?.colors?.accent) {
      brandColors.push({
        name: 'Accent',
        value: state.brandData.colors.accent,
        description: 'Brand accent color'
      });
    }
    
    return brandColors;
  };

  // Reset to brand color
  const resetToBrandColor = (colorValue: string) => {
    const resetCustomization = {
      backgroundType: 'color' as const,
      backgroundColor: colorValue,
      backgroundImage: '',
      backgroundPosition: 'center',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      gradientFrom: '#8b5cf6',
      gradientTo: '#06b6d4',
      gradientAngle: 135,
      overlayEnabled: false,
      overlayColor: '#000000',
      overlayOpacity: 50
    };
    
    setPreviewCustomization(resetCustomization);
    // Don't apply changes to section until Apply is clicked
    // applyCustomizationToSection(resetCustomization, false);
    setShowBrandColorOptions(false);
  };

  // Reset to transparent
  const resetToTransparent = () => {
    const defaultCustomization = {
      backgroundType: 'color' as const,
      backgroundColor: 'transparent',
      backgroundImage: '',
      backgroundPosition: 'center',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      gradientFrom: '#8b5cf6',
      gradientTo: '#06b6d4',
      gradientAngle: 135,
      overlayEnabled: false,
      overlayColor: '#000000',
      overlayOpacity: 50
    };
    
    setPreviewCustomization(defaultCustomization);
    // Don't apply changes to section until Apply is clicked
    // applyCustomizationToSection(defaultCustomization, false);
    setShowBrandColorOptions(false);
  };

  // Apply changes and save to applied state
  const applyChanges = () => {
    setAppliedCustomization(previewCustomization);
    applyCustomizationToSection(previewCustomization, true);
    onClose();
  };

  // Cancel changes and revert to applied state
  const cancelChanges = () => {
    setPreviewCustomization(appliedCustomization);
    applyCustomizationToSection(appliedCustomization, false);
    onClose();
  };

  if (!section) {
    return null;
  }

  const brandColors = getBrandColors();

  return (
    <>
      {/* Customizer Panel - NO BACKDROP */}
      <div
        className="fixed top-0 right-0 h-full w-96 shadow-2xl border-l backdrop-blur-lg z-50 overflow-y-auto bg-card border-border"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between p-6 border-b backdrop-blur-lg bg-card border-border">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-workspace-accent rounded-lg flex items-center justify-center">
              <Settings className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">Section Customizer</h3>
              <p className="text-xs text-muted-foreground">{section.title}</p>
            </div>
          </div>
          <button
            onClick={cancelChanges}
            className="p-2 rounded-lg transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          
          {/* Background Type Selection */}
          <div>
            <label className={`block text-sm font-medium mb-3 ${
              'text-foreground'
            }`}>Background Type</label>
            <div className="flex gap-2 w-full">
              <button
                onClick={() => updatePreviewSetting('backgroundType', 'color')}
                className={`flex-1 py-3 px-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
                  previewCustomization.backgroundType === 'color'
                    ? 'bg-workspace-accent text-white'
                    : 'bg-header-control text-foreground hover:bg-accent border border-border'
                }`}
              >
                <Palette className="w-4 h-4" />
                <span>Color</span>
              </button>
              <button
                onClick={() => updatePreviewSetting('backgroundType', 'image')}
                className={`flex-1 py-3 px-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
                  previewCustomization.backgroundType === 'image'
                    ? 'bg-workspace-accent text-white'
                    : 'bg-header-control text-foreground hover:bg-accent border border-border'
                }`}
              >
                <ImageIcon className="w-4 h-4" />
                <span>Image</span>
              </button>
              <button
                onClick={() => updatePreviewSetting('backgroundType', 'gradient')}
                className={`flex-1 py-3 px-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center space-x-2 ${
                  previewCustomization.backgroundType === 'gradient'
                    ? 'bg-workspace-accent text-white'
                    : 'bg-header-control text-foreground hover:bg-accent border border-border'
                }`}
              >
                <Sparkles className="w-4 h-4" />
                <span>Gradient</span>
              </button>
            </div>
          </div>

          {/* Preview Section */}
          <div>
            <label className={`block text-sm font-medium mb-3 ${
              'text-foreground'
            }`}>Preview</label>
            <div 
              className="w-full h-24 rounded-lg"
              style={generateBackgroundStyle(previewCustomization)}
            />
          </div>

          {/* Background Color Settings */}
          {previewCustomization.backgroundType === 'color' && (
            <div>
              <label className={`block text-sm font-medium mb-3 ${
                'text-foreground'
              }`}>Background Color</label>
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={previewCustomization.backgroundColor === 'transparent' ? '#ffffff' : previewCustomization.backgroundColor}
                  onChange={(e) => updatePreviewSetting('backgroundColor', e.target.value)}
                  className="w-16 h-12 rounded-lg border-2 border-border cursor-pointer"
                />
                <input
                  type="text"
                  value={previewCustomization.backgroundColor}
                  onChange={(e) => updatePreviewSetting('backgroundColor', e.target.value)}
                  className="flex-1 px-3 py-3 rounded-lg border text-sm bg-background border-input text-foreground placeholder-muted-foreground"
                  placeholder="transparent"
                />
                <button
                  onClick={() => updatePreviewSetting('backgroundColor', 'transparent')}
                  className={`px-3 py-3 text-sm rounded-lg border transition-colors ${
                    previewCustomization.backgroundColor === 'transparent'
                      ? 'bg-workspace-accent text-white border-workspace-accent'
                      : 'bg-background border-input text-foreground hover:bg-accent'
                  }`}
                >
                  None
                </button>
              </div>
            </div>
          )}

          {/* Background Image Settings */}
          {previewCustomization.backgroundType === 'image' && (
            <div className="space-y-4">
              {/* Image Upload Button */}
              <div>
                <label className={`block text-sm font-medium mb-3 ${
                  'text-foreground'
                }`}>Background Image</label>
                
                <button
                  onClick={() => setShowImageModal(true)}
                  className="w-full flex items-center justify-center space-x-2 p-6 border-2 border-dashed rounded-lg transition-colors border-border hover:border-workspace-accent bg-muted/50 text-foreground"
                >
                  <Upload className="w-5 h-5" />
                  <span>{previewCustomization.backgroundImage ? 'Change Background Image' : 'Upload Background Image'}</span>
                </button>
              </div>



              {/* Image Settings */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    'text-foreground'
                  }`}>Position</label>
                  <select
                    value={previewCustomization.backgroundPosition}
                    onChange={(e) => updatePreviewSetting('backgroundPosition', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border text-sm bg-background border-input text-foreground"
                  >
                    <option value="center">Center</option>
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                    <option value="top left">Top Left</option>
                    <option value="top right">Top Right</option>
                    <option value="bottom left">Bottom Left</option>
                    <option value="bottom right">Bottom Right</option>
                  </select>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    'text-foreground'
                  }`}>Size</label>
                  <select
                    value={previewCustomization.backgroundSize}
                    onChange={(e) => updatePreviewSetting('backgroundSize', e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border text-sm bg-background border-input text-foreground"
                  >
                    <option value="cover">Cover</option>
                    <option value="contain">Contain</option>
                    <option value="auto">Auto</option>
                    <option value="100% 100%">Stretch</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className={`block text-sm font-medium mb-2 ${
                  'text-foreground'
                }`}>Repeat</label>
                <select
                  value={previewCustomization.backgroundRepeat}
                  onChange={(e) => updatePreviewSetting('backgroundRepeat', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border text-sm bg-background border-input text-foreground"
                >
                  <option value="no-repeat">No Repeat</option>
                  <option value="repeat">Repeat</option>
                  <option value="repeat-x">Repeat X</option>
                  <option value="repeat-y">Repeat Y</option>
                </select>
              </div>
            </div>
          )}

          {/* Gradient Settings */}
          {previewCustomization.backgroundType === 'gradient' && (
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-3 ${
                  'text-foreground'
                }`}>Gradient</label>
                
                {/* Gradient Color Inputs */}
                <div className="space-y-4 mb-4">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${'text-muted-foreground'}`}>From</span>
                    <input
                      type="color"
                      value={previewCustomization.gradientFrom}
                      onChange={(e) => updateGradient(e.target.value, previewCustomization.gradientTo, previewCustomization.gradientAngle)}
                      className="h-10 w-16 p-0 border-0 rounded-md ring-0 outline-none bg-transparent cursor-pointer"
                    />
                    <input
                      type="text"
                      value={previewCustomization.gradientFrom}
                      onChange={(e) => updateGradient(e.target.value, previewCustomization.gradientTo, previewCustomization.gradientAngle)}
                      className="flex-1 px-3 py-2 rounded-lg border text-sm bg-background border-input text-foreground placeholder-muted-foreground"
                      placeholder="#8b5cf6"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${'text-muted-foreground'}`}>To</span>
                    <input
                      type="color"
                      value={previewCustomization.gradientTo}
                      onChange={(e) => updateGradient(previewCustomization.gradientFrom, e.target.value, previewCustomization.gradientAngle)}
                      className="h-10 w-16 p-0 border-0 rounded-md ring-0 outline-none bg-transparent cursor-pointer"
                    />
                    <input
                      type="text"
                      value={previewCustomization.gradientTo}
                      onChange={(e) => updateGradient(previewCustomization.gradientFrom, e.target.value, previewCustomization.gradientAngle)}
                      className="flex-1 px-3 py-2 rounded-lg border text-sm bg-background border-input text-foreground placeholder-muted-foreground"
                      placeholder="#06b6d4"
                    />
                  </div>
                </div>

                {/* Gradient Angle */}
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    'text-foreground'
                  }`}>Angle</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0"
                      max="360"
                      step="1"
                      value={previewCustomization.gradientAngle}
                      onChange={(e) => updateGradient(previewCustomization.gradientFrom, previewCustomization.gradientTo, parseInt(e.target.value))}
                      className={`w-full h-2 rounded-lg appearance-none cursor-pointer ${
                        'bg-muted'
                      }`}
                      style={{
                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${previewCustomization.gradientAngle / 360 * 100}%, hsl(var(--muted)) ${previewCustomization.gradientAngle / 360 * 100}%, hsl(var(--muted)) 100%)`
                      }}
                    />
                    <input
                      type="number"
                      min="0"
                      max="360"
                      value={previewCustomization.gradientAngle}
                      onChange={(e) => updateGradient(previewCustomization.gradientFrom, previewCustomization.gradientTo, parseInt(e.target.value))}
                      className="w-20 px-3 py-2 rounded-lg border text-sm bg-background border-input text-foreground"
                    />
                  </div>
                </div>

                {/* Gradient Preview */}

              </div>
            </div>
          )}

          {/* Background Overlay Settings */}
          <div className="border rounded-lg p-4 border-border">
            {/* Overlay Toggle */}
            <div className="flex items-center justify-between mb-4">
              <label className={`text-sm font-medium ${
                'text-foreground'
              }`}>Background Overlay</label>
              <button
                onClick={() => updatePreviewSetting('overlayEnabled', !previewCustomization.overlayEnabled)}
                className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  previewCustomization.overlayEnabled
                    ? 'bg-workspace-accent text-white'
                    : 'bg-header-control text-foreground hover:bg-accent'
                }`}
              >
                {previewCustomization.overlayEnabled ? (
                  <>
                    <Eye className="w-4 h-4" />
                    <span>Enabled</span>
                  </>
                ) : (
                  <>
                    <EyeOff className="w-4 h-4" />
                    <span>Disabled</span>
                  </>
                )}
              </button>
            </div>


            {/* Overlay Controls - Only show when enabled */}
            {previewCustomization.overlayEnabled && (
              <div className="space-y-4">

                  {/* Overlay Color */}
                  <div>
                    <label className={`block text-xs font-medium mb-2 ${
                      'text-muted-foreground'
                    }`}>Overlay Color</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={previewCustomization.overlayColor}
                        onChange={(e) => updatePreviewSetting('overlayColor', e.target.value)}
                        className="w-12 h-8 rounded border-2 border-border cursor-pointer"
                      />
                      <input
                        type="text"
                        value={previewCustomization.overlayColor}
                        onChange={(e) => updatePreviewSetting('overlayColor', e.target.value)}
                        className="flex-1 px-3 py-2 rounded border text-sm bg-background border-input text-foreground placeholder-muted-foreground"
                        placeholder="#000000"
                      />
                    </div>
                  </div>

                  {/* Overlay Opacity - Fixed slider jumping issue */}
                  <div>
                    <label className={`block text-xs font-medium mb-2 ${
                      'text-muted-foreground'
                    }`}>Overlay Opacity: {previewCustomization.overlayOpacity}%</label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={previewCustomization.overlayOpacity}
                      onChange={(e) => updatePreviewSetting('overlayOpacity', parseInt(e.target.value))}
                      className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${previewCustomization.overlayOpacity}%, #e5e7eb ${previewCustomization.overlayOpacity}%, #e5e7eb 100%)`
                      }}
                    />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>0%</span>
                      <span>50%</span>
                      <span>100%</span>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 flex items-center justify-between p-6 border-t backdrop-blur-lg bg-card border-border">
          {/* Reset Button with Brand Color Options */}
          <div className="relative">
            <button
              onClick={() => setShowBrandColorOptions(!showBrandColorOptions)}
              className="flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Reset</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${
                showBrandColorOptions ? 'rotate-180' : ''
              }`} />
            </button>

            {/* Brand Color Options Dropdown */}
            {showBrandColorOptions && (
              <>
                {/* Backdrop to close dropdown */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowBrandColorOptions(false)}
                />

                {/* Dropdown Menu */}
                <div
                  className="absolute bottom-full left-0 mb-2 rounded-lg border shadow-lg min-w-[200px] z-50 bg-card border-border"
                  >
                    {/* Transparent Option */}
                    <button
                      onClick={resetToTransparent}
                      className="w-full px-3 py-2 text-left text-sm transition-colors flex items-center space-x-3 text-foreground hover:bg-accent"
                    >
                      <div className="w-4 h-4 rounded border-2 border-border bg-transparent" />
                      <div>
                        <div className="font-medium">Transparent</div>
                        <div className={`text-xs ${
                          'text-muted-foreground'
                        }`}>No background color</div>
                      </div>
                    </button>

                    {/* Brand Colors */}
                    {brandColors.map((color) => (
                      <button
                        key={color.name}
                        onClick={() => resetToBrandColor(color.value)}
                        className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center space-x-3 ${
                          'text-foreground hover:bg-accent'
                        }`}
                      >
                        <div 
                          className="w-4 h-4 rounded border border-border" 
                          style={{ backgroundColor: color.value }}
                        />
                        <div>
                          <div className="font-medium">{color.name}</div>
                          <div className={`text-xs ${
                            'text-muted-foreground'
                          }`}>{color.description}</div>
                        </div>
                      </button>
                    ))}

                    {/* No Brand Colors Message */}
                    {brandColors.length === 0 && (
                      <div className={`px-3 py-2 text-xs ${
                        'text-muted-foreground'
                      }`}>
                        No brand colors available. Set brand colors in Brand Settings.
                      </div>
                    )}
                  </div>
                </>
              )}
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={cancelChanges}
              className="px-4 py-2 rounded-lg transition-colors text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              Cancel
            </button>
            <button
              onClick={applyChanges}
              className="flex items-center space-x-2 px-4 py-2 bg-workspace-accent text-white rounded-lg hover:bg-workspace-accent-hover transition-colors"
            >
              <Save className="w-4 h-4" />
              <span>Apply</span>
            </button>
          </div>
        </div>
      </div>

      {/* Image Upload Modal */}
      <ImageUploadModal
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
        onImageSelect={handleImageSelect}
        isGlobalDarkMode={isGlobalDarkMode}
        title="Upload Background Image"
        acceptedFormats={['.jpg', '.jpeg', '.png', '.gif', '.webp']}
        maxFileSize={10}
        showUnsplash={true}
        initialSearchQuery="background pattern"
      />
    </>
  );
};